import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Observable, of, BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { map, filter, tap, switchMap, distinctUntilChanged, take } from 'rxjs/operators';
import { isNil } from 'lodash';

import { LeftSidebarService } from '../../Common/Layout/services/left-sidebar.service';
import { DropdownOptionModel } from '../../Common/Forms/models/dropdown-option.model';
import { LeftSidebarListItem } from '../../Common/Layout/models/left-sidebar-list-item.model';
import { LeftSidebarListItemButton } from '../../Common/Layout/models/left-sidebar-list-item-button.model';
import { LeftSidebarLocalizations } from '../../Common/Layout/models/left-sidebar-localizations.model';
import { DisplayGroupModel } from '../../Common/Layout/models/display-group.model';
import { DisplayGroupsService } from '../../Common/Layout/services/display-groups.service';
import { DisplayGroupTypeEnum } from '../../api/displayGroups/enums/display-group-type.enum';
import { UsersService } from '../../Core/Users/services/users.service';
import { LocalizationService } from '../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../Core/Users/services/current-user-state.service';
import { DiscoverRoutingService } from '../../Discover/DiscoverRouter/services/discover-routing.service';
import { AmplifyRoutingService } from '../../Amplify/Core/services/amplify-routing.service';
import { userPermissionsEnum } from '../../Core/Users/enums/user-permissions.enum';
import { StoriesConstants } from '../../News/Newsroom/components/stories/stories-constants';
import { Mode } from '../../Common/Layout/enums/mode.enum';
import { CoreDialogService } from '../../Core/Dialogs/services/dialog.service';
import { ViAgentDialogComponent } from '../components/ViAgentDialog/vi-agent-dialog.component';
import { FullProfileAnimationModel } from '../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../Core/Dialogs/enums/overlay-animation.enum';
import { ViMonitorService } from './vi-monitor.service';
import { ViRoutingService } from './vi-routing.service';
import { ViMonitorStatusEnum } from '../enums/vi-monitor-status.enum';
import { ViMonitorListItem } from '../models/vi-monitor-list-item.model';
import { FilterTypeCheckboxModel } from '../../Common/Filter/models/filter-type-checkbox.model';
import { ConfirmDialogModel } from '../../Common/Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { TextInputDialogModel } from '../../Common/Dialogs/models/text-input-dialog.model';
import { TextInputDialogComponent } from '../../Common/Dialogs/components/TextInputDialog/text-input-dialog.component';
import { NewResourceDialogModel } from '../../Common/Dialogs/models/new-resource-dialog.model';
import { NewResourceDialogComponent } from '../../Common/Dialogs/components/NewResourceDialog/new-resource-dialog.component';
import { ListEntitiesVisibilityEnum } from '../../Common/MonitorShared/enum/list-entities-visibility.enum';
import { DateQueryModel } from '../../Common/Forms/models/date-query.model';
import { ActionsEnum } from '../../Common/Basic/enums/actions.enum';
import { messageSeverityEnum } from '../../Common/Misc/enums/message-severity.enum';
import { ViRouteModel } from '../models/vi-route.model';
import { ViRouteType } from '../enums/vi-route-type.enum';

@Injectable()
export class ViLeftSidebarService extends LeftSidebarService {
    private readonly viDropdownItem = new DropdownOptionModel(
        'Core.navigation.mediaRelations.visibilityIntelligence',
        Mode.visibilityIntelligence
    );
    private readonly dropdownList: DropdownOptionModel[] = [
        new DropdownOptionModel('Core.navigation.mediaRelations.savedSearches', Mode.searches),
        new DropdownOptionModel('Core.navigation.mediaRelations.targetedLists', Mode.targetingLists),
        new DropdownOptionModel('Core.navigation.mediaRelations.distributions', Mode.distributions),
        new DropdownOptionModel(StoriesConstants.sidebarSectionNewsroom, Mode.newsroom),
        new DropdownOptionModel('Core.navigation.mediaRelations.newswire', Mode.wire),
        new DropdownOptionModel('Amplify.leftSidebar.wire.review', Mode.editorial),
        new DropdownOptionModel('Core.navigation.mediaRelations.privateContacts', Mode.privateContacts),
        this.viDropdownItem
    ];
    private readonly localizations: LeftSidebarLocalizations = {
        amplifyButton: 'VisibilityIntelligence.lhp.amplifyButton',
        secondaryAmplifyButton: 'VisibilityIntelligence.lhp.secondaryAmplifyButton',
        itemsHeading: 'VisibilityIntelligence.lhp.itemsHeading',
        statusHeading: 'VisibilityIntelligence.lhp.statusHeading',
        noItems: 'VisibilityIntelligence.lhp.noItems'
    };

    private readonly itemActionRename: LeftSidebarListItemButton = {
        icon: 'fa-regular fa-duotone fa-pen',
        key: ActionsEnum.rename,
        label: 'Lhp.actions.rename',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.rename, icon: ''})
    };
    private readonly itemActionCopy: LeftSidebarListItemButton = {
        icon: 'fa-kit-duotone fa-agpr-copy',
        key: ActionsEnum.copy,
        label: 'Lhp.actions.copy',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.copy, icon: ''})
    };
    private readonly itemActionHide: LeftSidebarListItemButton = {
        icon: 'fa-duotone fa-regular fa-eye-slash',
        key: ActionsEnum.hide,
        label: 'VisibilityIntelligence.lhp.monitorActions.hide',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.hide, icon: ''})
    };
    private readonly itemActionShow: LeftSidebarListItemButton = {
        icon: 'fa-duotone fa-regular fa-eye',
        key: ActionsEnum.show,
        label: 'VisibilityIntelligence.lhp.monitorActions.show',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.show, icon: ''})
    };
    private readonly itemActionFavorite: LeftSidebarListItemButton = {
        icon: 'fa-duotone fa-regular fa-star',
        key: ActionsEnum.favorite,
        label: 'VisibilityIntelligence.lhp.monitorActions.favorite',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.favorite, icon: ''})
    };
    private readonly itemActionUnfavorite: LeftSidebarListItemButton = {
        icon: 'fa-solid fa-star',
        key: ActionsEnum.unfavorite,
        label: 'VisibilityIntelligence.lhp.monitorActions.unfavorite',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.unfavorite, icon: ''})
    };
    private readonly itemActionMove: LeftSidebarListItemButton = {
        icon: 'fa-duotone fa-regular fa-folder',
        key: ActionsEnum.move,
        label: 'VisibilityIntelligence.lhp.monitorActions.move',
        command: (event: any) => this.moveListItem(event.data)
    };
    private readonly itemActionDelete: LeftSidebarListItemButton = {
        icon: 'fa-duotone fa-regular fa-trash',
        key: ActionsEnum.delete,
        label: 'VisibilityIntelligence.lhp.monitorActions.delete',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.delete, icon: ''})
    };
    private readonly buttons: LeftSidebarListItemButton[] = [
        this.itemActionRename,
        this.itemActionCopy,
        this.itemActionFavorite,
        this.itemActionUnfavorite,
        this.itemActionMove,
        this.itemActionHide,
        this.itemActionShow,
        this.itemActionDelete,
    ];

    private dropdownItems: DropdownOptionModel[] = [this.viDropdownItem];
    private dropdownItemsProvider: BehaviorSubject<DropdownOptionModel[]> = new BehaviorSubject<DropdownOptionModel[]>(this.dropdownItems);
    private selectedDropdownItem: DropdownOptionModel = this.viDropdownItem;
    private selectedDropdownItemProvider: BehaviorSubject<DropdownOptionModel> = new BehaviorSubject<DropdownOptionModel>(this.selectedDropdownItem);

    private localizationsProvider: BehaviorSubject<LeftSidebarLocalizations> = new BehaviorSubject(this.localizations);

    private selectedListItem: LeftSidebarListItem;
    private selectedListItemProvider: BehaviorSubject<LeftSidebarListItem | null>;

    private listIsCompleteProvider: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    private reorderEnabledProvider: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private hiddenListItemsProvider: BehaviorSubject<LeftSidebarListItem[]> = new BehaviorSubject<LeftSidebarListItem[]>([]);

    private subscriptions: Subscription[] = [];

    private readonly statusFilter = new FilterTypeCheckboxModel<ViMonitorStatusEnum>(
        'status',
        'VisibilityIntelligence.lhp.filters.status',
        [
            { label: 'VisibilityIntelligence.lhp.status.active', value: ViMonitorStatusEnum.active },
            { label: 'VisibilityIntelligence.lhp.status.paused', value: ViMonitorStatusEnum.paused },
            { label: 'VisibilityIntelligence.lhp.status.draft', value: ViMonitorStatusEnum.draft }
        ]
    );

    private viMonitors: ViMonitorListItem[] = [];

    constructor(
        protected usersService: UsersService,
        protected displayGroupsService: DisplayGroupsService,
        protected localizationService: LocalizationService,
        private currentUserStateService: CurrentUserStateService,
        private discoverRoutingService: DiscoverRoutingService,
        private amplifyRoutingService: AmplifyRoutingService,
        private router: Router,
        private coreDialogService: CoreDialogService,
        private viMonitorService: ViMonitorService,
        private viRoutingService: ViRoutingService
    ) {
        super(usersService, displayGroupsService, localizationService);

        this.currentMode = Mode.visibilityIntelligence;
        this.modeProvider$ = new BehaviorSubject<Mode>(this.currentMode);

        this.listItems = null;
        this.listItemsProvider = new BehaviorSubject<LeftSidebarListItem[]>([]);

        this.listItemGroups = [];
        this.listItemGroupsProvider = new BehaviorSubject<DisplayGroupModel[]>([]);

        this.lhpSortFields = [this.sortFieldName, this.sortFieldCreatedDate, this.sortFieldStatus];
        this.lhpSortFields$.next(this.lhpSortFields.slice());

        this.selectedSortField = this.sortFieldName;
        this.selectedSortField$.next(this.selectedSortField);


        this.selectedListItem = null;
        this.selectedListItemProvider = new BehaviorSubject(this.selectedListItem);

        this.setQuickFilterPlaceholder('VisibilityIntelligence.lhp.searchPlaceholder');
        this.getVisibilityFilterOptions(this.visibilityFilter);
        this.setFilters([
            this.nameFilter,
            this.createdByFilter,
            this.createdDateFilter,
            this.modifiedByFilter,
            this.modifiedDateFilter,
            this.statusFilter,
            this.visibilityFilter
        ]);

        this.primaryHeading = 'VisibilityIntelligence.hype.headline';
        this.primaryHeading$.next(this.primaryHeading);

        this.subscriptions.push(
            this.currentUserStateService.getPermissions().pipe(filter(x => !!x)).subscribe(permissions => {
                this.dropdownItems = this.getDropdownList(permissions);
                this.dropdownItemsProvider.next(this.dropdownItems);
            }),
            combineLatest([
                this.viRoutingService.getCurrentRoute().pipe(
                    filter(route => !!route),
                    switchMap(() => this.displayGroupsService.setTypeAndGetGroups(DisplayGroupTypeEnum.viMonitor))
                ),
                this.viMonitorService.all$
            ]).subscribe(([groups, monitors]) => {
                this.listItemGroups = groups.filter(group => group.type === DisplayGroupTypeEnum.viMonitor);
                this.viMonitors = monitors;
                this.populateFilterOptions(this.viMonitors);
                this.refreshListItems();
            }),
            this.viRoutingService.getCurrentRoute().pipe(
                filter(route => !!route),
                map(route => this.selectedValueFor(route)),
                // reports <-> queries-and-answers is the same monitor: no selection change
                distinctUntilChanged()
            ).subscribe(value => this.applySelection(value))
        );
    }

    override applyFilters(): void {
        super.applyFilters();
        this.refreshListItems();
    }

    private refreshListItems(): void {
        const filteredMonitors = this.filterMonitors(this.viMonitors);

        const hiddenMonitors = this.getHiddenMonitorsFromFilteredMonitors(filteredMonitors);
        this.hiddenListItemsProvider.next(hiddenMonitors.map(monitor => this.toListItem(monitor)));

        const visibleMonitors = this.applyVisibilityFilter(filteredMonitors);
        const listItems = visibleMonitors.map(monitor => this.toListItem(monitor));
        this.setListItems(listItems);
    }

    getHiddenListItems(): Observable<LeftSidebarListItem[]> {
        return this.hiddenListItemsProvider;
    }

    setVisibilityFilter(visibility: ListEntitiesVisibilityEnum = ListEntitiesVisibilityEnum.all): void {
        this.visibilityFilter.setCurrentValue(visibility);
        this.applyFilters();
    }

    private applyVisibilityFilter(monitors: ViMonitorListItem[]): ViMonitorListItem[] {
        switch (this.visibilityFilter.getValue()) {
            case ListEntitiesVisibilityEnum.all:
                return monitors;
            case ListEntitiesVisibilityEnum.hidden:
                return monitors.filter(monitor => !!monitor.hidden);
            case ListEntitiesVisibilityEnum.visible:
            default:
                return monitors.filter(monitor => !monitor.hidden);
        }
    }

    private getHiddenMonitorsFromFilteredMonitors(monitors: ViMonitorListItem[]): ViMonitorListItem[] {
        switch (this.visibilityFilter.getValue()) {
            case ListEntitiesVisibilityEnum.all:
            case ListEntitiesVisibilityEnum.hidden:
                return [];
            case ListEntitiesVisibilityEnum.visible:
            default:
                return monitors.filter(monitor => monitor.hidden);
        }
    }

    private populateFilterOptions(monitors: ViMonitorListItem[]): void {
        const createdByIds = Array.from(new Set(monitors.map(m => m.createdByUserId).filter((id): id is number => !!id)));
        const modifiedByIds = Array.from(new Set(monitors.map(m => m.modifiedByUserId).filter((id): id is number => !!id)));
        const allIds = Array.from(new Set([...createdByIds, ...modifiedByIds]));

        if (!allIds.length) {
            return;
        }

        this.usersService.getUsers(allIds).subscribe(users => {
            this.createdByFilter.options = createdByIds
                .map(id => {
                    const user = users.find(u => u.id === id);
                    return user ? { label: user.getDisplayName(), value: id } : null;
                })
                .filter(option => !!option);

            this.modifiedByFilter.options = modifiedByIds
                .map(id => {
                    const user = users.find(u => u.id === id);
                    return user ? { label: user.getDisplayName(), value: id } : null;
                })
                .filter(option => !!option);
        });
    }

    private filterMonitors(monitors: ViMonitorListItem[]): ViMonitorListItem[] {
        let filtered = monitors;

        const nameTerms = this.nameFilter.getValue();
        if (nameTerms?.length) {
            filtered = filtered.filter(monitor => {
                const name = (monitor.name ?? '').toLocaleLowerCase();
                return nameTerms.some(term => name.includes(term.toLocaleLowerCase()));
            });
        }

        const statusValues = this.statusFilter.getValue();
        if (statusValues?.length) {
            filtered = filtered.filter(monitor => statusValues.includes(monitor.status));
        }

        const createdByValues = this.createdByFilter.getValue();
        if (createdByValues?.length) {
            filtered = filtered.filter(monitor => !isNil(monitor.createdByUserId) && createdByValues.includes(monitor.createdByUserId));
        }

        const modifiedByValues = this.modifiedByFilter.getValue();
        if (modifiedByValues?.length) {
            filtered = filtered.filter(monitor => !isNil(monitor.modifiedByUserId) && modifiedByValues.includes(monitor.modifiedByUserId));
        }

        const createdDateFilter = this.createdDateFilter.getValue();
        if (createdDateFilter) {
            filtered = filtered.filter(monitor =>
                DateQueryModel.isInDateQuery(monitor.createdDate as Date, createdDateFilter, new Date(), this.datefilterBufferMinutes)
            );
        }

        const modifiedDateFilter = this.modifiedDateFilter.getValue();
        if (modifiedDateFilter) {
            filtered = filtered.filter(monitor =>
                DateQueryModel.isInDateQuery(monitor.modifiedDate as Date, modifiedDateFilter, new Date(), this.datefilterBufferMinutes)
            );
        }

        return filtered;
    }

    private toListItem(monitor: ViMonitorListItem): LeftSidebarListItem {
        // can only delete when status is draft or paused
        return monitor.toLeftSidebarListItem(this.buttons.filter(button => {
            if (button.key === ActionsEnum.delete) {
                return ((monitor.status === ViMonitorStatusEnum.draft) || (monitor.status === ViMonitorStatusEnum.paused));
            }
            return true;
        }));
    }

    private getDropdownList(permissions: userPermissionsEnum[] = []): DropdownOptionModel<Mode>[] {
        const list: DropdownOptionModel[] = this.dropdownList.filter(item => {
            if (item.value === Mode.newsroom && !permissions.includes(userPermissionsEnum.newsroom)) {
                return false;
            }
            if (item.value === Mode.wire && !permissions.includes(userPermissionsEnum.newswireplus)) {
                return false;
            }
            if (item.value === Mode.editorial
                && (!permissions.includes(userPermissionsEnum.newswireplus) || !permissions.includes(userPermissionsEnum.wireEditorial))) {
                return false;
            }
            return true;
        });

        return list;
    }

    getDropdownItems(): Observable<DropdownOptionModel[]> {
        return this.dropdownItemsProvider;
    }

    getSelectedDropdownItem(): Observable<DropdownOptionModel> {
        return this.selectedDropdownItemProvider;
    }

    selectDropdownItem(item: DropdownOptionModel): void {
        if (this.dropdownItems.indexOf(item) < 0) {
            throw new Error('Item is not in the list');
        }

        switch (item.value) {
            case Mode.searches:
                this.discoverRoutingService.gotoTargetingHype();
                break;
            case Mode.targetingLists:
                this.discoverRoutingService.gotoListHype();
                break;
            case Mode.privateContacts:
                this.discoverRoutingService.gotoPrivateContacts();
                break;
            case Mode.distributions:
                this.discoverRoutingService.gotoDistributionHype();
                break;
            case Mode.wire:
                this.amplifyRoutingService.gotoWireHype();
                break;
            case Mode.editorial:
                this.amplifyRoutingService.gotoEditorialHype();
                break;
            case Mode.newsroom:
                this.router.navigate([StoriesConstants.newsroomRootPath]);
                break;
            default:
                break;
        }
    }

    amplifyButtonClicked(): void {
        this.viRoutingService.gotoNew();
    }

    secondaryAmplifyButtonClicked(): void {
        this.coreDialogService.open(ViAgentDialogComponent, {
            inputs: {
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                ),
                overlayContainer: document.getElementById('main-content')
            }
        });
    }

    getListItems(): Observable<LeftSidebarListItem[]> {
        return this.listItemsProvider;
    }

    getListIsComplete(): Observable<boolean> {
        return this.listIsCompleteProvider;
    }

    getMoreListItems(): void {}

    getSelectedListItem(): Observable<LeftSidebarListItem | null> {
        return this.selectedListItemProvider;
    }

    selectListItem(item: LeftSidebarListItem) {
        switch (item.status) {
            case ViMonitorStatusEnum.draft:
                this.viRoutingService.gotoEdit(item.value);
                break;
            case ViMonitorStatusEnum.active:
            case ViMonitorStatusEnum.paused:
                this.viRoutingService.gotoReports(item.value);
                break;
            default:
                break;
        }
    }

    listItemAction(item: LeftSidebarListItem, button: LeftSidebarListItemButton, fromLHP?: boolean): void {
        const monitorId = item.value;

        switch (button.key) {
            case ActionsEnum.rename:
                this.openRenameMonitorDialog(item);
                break;
            case ActionsEnum.copy:
                this.confirmCopyMonitor(item);
                break;
            case ActionsEnum.move:
                this.moveListItem(item);
                break;
            case ActionsEnum.favorite:
                this.viMonitorService.updateFavourites([monitorId], []).subscribe();
                break;
            case ActionsEnum.unfavorite:
                this.viMonitorService.updateFavourites([], [monitorId]).subscribe();
                break;
            case ActionsEnum.hide:
            case ActionsEnum.show:
                this.viMonitorService.updateVisibility(item.value, button.key).subscribe();
                break;
            case ActionsEnum.delete:
                this.confirmDeleteMonitor(item);
                break;
            default:
                break;
        }
    }

    private openRenameMonitorDialog(item: LeftSidebarListItem) {
        const id = item.value;
        const io: TextInputDialogModel = {
            inputs: {
                id: 'renameViMonitor',
                heading: 'VisibilityIntelligence.lhp.renameDialog.heading',
                description: 'VisibilityIntelligence.lhp.renameDialog.description',
                placeholder: 'VisibilityIntelligence.lhp.renameDialog.placeholder',
                initialValue: item.label,
                okLabel: 'Lhp.actions.rename',
                observableFormValidator: (name: string) => {
                    return this.viMonitorService.isNameDuplicated$(name, id).pipe(
                        map((isDuplicate) => {
                            if (isDuplicate) {
                                return ['VisibilityIntelligence.lhp.renameDialog.duplicateNameError'];
                            } else {
                                return [];
                            }
                        })
                    );
                },
                submit: (name: string) => this.viMonitorService.rename(id, name)
            }
        };
        this.coreDialogService.open(TextInputDialogComponent, io);
    }

    private confirmCopyMonitor(item: LeftSidebarListItem) {
        const id = item.value;
        const io: NewResourceDialogModel = {
            inputs: {
                id: 'copyViMonitor',
                heading: 'VisibilityIntelligence.lhp.copyDialog.heading',
                initialValue: `${item.label}${this.localizationService.transform('Common.dialog.newResource.copy.suffix')}`,
                currentGroup: item.groupId,
                okLabel: 'Common.dialog.newResource.copy',
                observableFormValidator: (name: string) => {
                    return this.viMonitorService.isNameDuplicated$(name, id).pipe(
                        map((isDuplicate) => {
                            if (isDuplicate) {
                                return ['VisibilityIntelligence.lhp.renameDialog.duplicateNameError'];
                            } else {
                                return [];
                            }
                        })
                    );
                },
                submit: (name: string, group?: DisplayGroupModel) => {
                    return this.viMonitorService.getById(item.value).pipe(
                        filter(monitor => !!monitor?.id),
                        switchMap(monitor => {
                            const copy = monitor.clone();
                            copy.name = name;
                            copy.isHidden = false;
                            copy.isFavourite = false;
                            copy.displayGroupId = group?.id;
                            return this.viMonitorService.create(copy);
                        }),
                        switchMap(newId => {
                            return this.viMonitorService.refreshAllManual().pipe(
                                filter(x => !!x),
                                take(1),
                                tap(() => this.viRoutingService.gotoEdit(newId))
                            );
                        })
                    );
                }
            }
        };
        this.coreDialogService.open(NewResourceDialogComponent, io);
    }

    private confirmDeleteMonitor(item: LeftSidebarListItem): void {
        const confirmConfig: ConfirmDialogModel = {
            inputs: {
                id: 'deleteMonitorConfirm',
                heading: 'VisibilityIntelligence.lhp.deleteConfirm.heading',
                message: this.localizationService.transform('VisibilityIntelligence.lhp.deleteConfirm.message', { name: item.label }),
                alertMessage: 'VisibilityIntelligence.lhp.deleteConfirm.alertMessage',
                alertType: messageSeverityEnum.warning,
                okLabel: 'Core.button.delete'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.viMonitorService.delete(item.value).subscribe();
                    }
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
    }

    moveListItem(item: LeftSidebarListItem) {
        const groupId = item.groupId ?? null;
        const showUncategorized = !!item.groupId;

        this.displayGroupsService.openGroupSelectionDialog(groupId, showUncategorized).subscribe((group: DisplayGroupModel) => {
            // @TODO fix nested subscribe
            // eslint-disable-next-line rxjs/no-nested-subscribe
            this.viMonitorService.updateGroup([item.value], group).subscribe();
        });
    }

    getReorderEnabled(): Observable<boolean> {
        return this.reorderEnabledProvider;
    }

    setListItems(items: LeftSidebarListItem[], listIsComplete?: boolean) {
        this.listItems = items;
        this.listItemsProvider.next(this.listItems);
        this.groupListItems();
    }

    handleGroupDelete(ids: number[]): void {
        this.viMonitorService.updateGroup(ids, null, false).subscribe();
    }

    getLocalizations(): Observable<LeftSidebarLocalizations> {
        return this.localizationsProvider;
    }

    isAmplifyButtonEnabled(): Observable<boolean> {
        return of(true);
    }

    private applySelection(value: number): void {
        if (value) {
            this.selectedListItem = new LeftSidebarListItem();
            this.selectedListItem.value = value;
        } else {
            this.selectedListItem = null;
        }

        this.selectedListItemProvider.next(this.selectedListItem);
    }

    /**
     * The monitor id the sidebar should highlight, or null when nothing is selected.
     */
    private selectedValueFor(route: ViRouteModel): number {
        switch (route.type) {
            case ViRouteType.EDIT:
            case ViRouteType.REPORTS:
            case ViRouteType.QUERIES_AND_ANSWERS:
                return route.value || null;

            case ViRouteType.HYPE:
            case ViRouteType.NEW:
            default:
                return null;
        }
    }
}
